//@flow
import * as Immutable from 'immutable';

import { Contact } from 'common/models/flow/Contact';
import type {
    ContactModelType,
    ContactTypeEnum
} from 'common/models/flow/Contact';
import EntityTypeEnum from 'common/enums/EntityTypeEnum';

import { PhoneNumber } from './PhoneNumber';
import { TeamId } from './TeamId';
import { EmailAddress } from './EmailAddress';
import DataUtils from 'common/utils/DataUtils';
import StringUtils from 'common/utils/StringUtils';
import RegExUtil from 'common/utils/RegExUtil';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';

//Validation stuff
import {
    validatable,
    validate,
    validateAll,
    validateId,
    softValidateAll
} from 'common/utils/ValidationUtil';
import I18nHelper from 'common/helpers/I18nHelper';

import { AuditMetadata } from './AuditMetadata';
import { ENTITY_NAME_ID } from 'common/constants/entity/contactMaster';

export const ContactType = new EntityTypeEnum({
    Customer: 'CUSTOMER',
    Consignee: 'CONSIGNEE',
    SalesRepresentative: 'SALES_REPRESENTATIVE',
    WarehouseManager: 'WAREHOUSE_MANAGER',
    Distributor: 'DISTRIBUTOR',
    Transporter: 'TRANSPORTER',
    Supplier: 'SUPPLIER',
    Retailer: 'RETAILER'
});

const translationsNamespace = ['entityManagement'];

const nsTranslate = I18nHelper.translate.bind(null, translationsNamespace);

@validatable
class InternalContact extends Contact {
    static fromJS(json: ContactModelType): InternalContact {
        const state: Object = Object.assign({}, json);
        state.phoneNumber = PhoneNumber.fromJS(state.phoneNumber);
        state.landlineNumber = state.landlineNumber
            ? PhoneNumber.fromJS(state.landlineNumber)
            : state.landlineNumber;
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.emailAddress = state.emailAddress
            ? EmailAddress.fromJS(state.emailAddress)
            : state.emailAddress;
        state.additionalPhoneNumbers = Immutable.List(
            state.additionalPhoneNumbers
        ).map(item => PhoneNumber.fromJS(item));
        state.additionalEmailAddresses = Immutable.List(
            state.additionalEmailAddresses
        ).map(item => EmailAddress.fromJS(item));
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.customProperties = Immutable.Map(state.customProperties);
        return new this(Immutable.Map(state));
    }

    @validateAll
    setAllValidationErrors(): this {
        return this;
    }

    getCellValidationMessage({ header, validationData }: Object) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    @validate
    setCode(code: string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    validateCode() {
        const code = this.code || this.id;
        if (StringUtils.isEmpty(code)) {
            return nsTranslate('entityValidationErrors.codeEmpty');
        }
        if (code.trim().length < 2) {
            return nsTranslate('entityValidationErrors.codeLessThan2');
        }
        if (code.trim().length > 32) {
            return nsTranslate('entityValidationErrors.codeGreaterThan32');
        }
    }

    validateId() {
        const { id } = this;
        return validateId(id);
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName() {
        if (!this.name || this.name.trim().length === 0) {
            return nsTranslate('contactValidations.nameEmpty');
        }

        if (!this.name || this.name.length > 128) {
            return nsTranslate('contactValidations.nameOverLimit');
        }
    }

    @validate
    setPhoneNumber(phoneNumber: PhoneNumber): this {
        return super.setPhoneNumber(phoneNumber);
    }

    validatePhoneNumber() {
        if (
            DataUtils.isEmpty(this.phoneNumber) ||
            DataUtils.isEmpty(this.phoneNumber.phoneNumber) ||
            DataUtils.isEmpty(this.phoneNumber.phoneNumber.trim())
        ) {
            return nsTranslate('contactValidations.phoneNumberEmpty');
        }
        return this.phoneNumber.validatePhoneNumber();
    }

    @validate
    setAdditionalEmailAddresses(email: Immutable.List<EmailAddress>): this {
        return super.setAdditionalEmailAddresses(email);
    }

    validateAdditionalEmailAddresses() {
        const { additionalEmailAddresses } = this;
        if (
            !DataUtils.isEmpty(additionalEmailAddresses) &&
            !DataUtils.isEmpty(
                additionalEmailAddresses.getIn([0, 'emailAddress'])
            )
        ) {
            return additionalEmailAddresses.get(0).validateEmailAddress();
        }
    }

    @validate
    setAdditionalPhoneNumbers(phoneNumber: Immutable.List<PhoneNumber>): this {
        return super.setAdditionalPhoneNumbers(phoneNumber);
    }

    validateAdditionalPhoneNumbers(): ?string {
        const { additionalPhoneNumbers } = this;
        const isNumberListNonEmpty =
            !DataUtils.isEmpty(additionalPhoneNumbers) &&
            additionalPhoneNumbers.every(
                number => !DataUtils.isEmpty(number.phoneNumber?.trim())
            );
        if (isNumberListNonEmpty) {
            const invalidNumber = additionalPhoneNumbers.find(number =>
                number.validatePhoneNumber()
            );
            return invalidNumber?.validatePhoneNumber();
        }
    }

    @validate
    setLandlineNumber(landlineNumber: PhoneNumber): this {
        return super.setLandlineNumber(landlineNumber);
    }

    validateLandlineNumber() {
        const { landlineNumber } = this;
        if (landlineNumber && !DataUtils.isEmpty(landlineNumber.phoneNumber)) {
            return landlineNumber.validatePhoneNumber();
        }
    }

    @validate
    setType(type: ContactTypeEnum): this {
        return super.setType(type);
    }

    contactTypes: Array<string> = []; // caching contact types values

    translatedContactTypes: Array<string> = []; // caching contact types translated values

    constructCachedContactType = ({
        value,
        label
    }: {
        value: string,
        label: string
    }) => {
        this.contactTypes.push(value);
        this.translatedContactTypes.push(label);
    };

    validateType() {
        const { type } = this;
        if (DataUtils.isEmpty(type)) {
            return nsTranslate('entityValidationErrors.typeEmpty', {
                entityNameId: ENTITY_NAME_ID
            });
        }
        if (!this.contactTypes.length) {
            ContactType.getOptions({
                translationKey: 'contactProperties.contactType',
                optionFormatter: this.constructCachedContactType
            });
        }
        if (!this.contactTypes.includes(type)) {
            return nsTranslate('entityValidationErrors.typeEnumValidation', {
                typeList: this.translatedContactTypes.join(', '),
                entityNameId: ENTITY_NAME_ID
            });
        }
        return null;
    }

    @softValidateAll
    hasValidationErrors() {
        if (this.phoneNumber) {
            return this.phoneNumber.hasValidationErrors();
        }
        return false;
    }

    get phoneNumber(): PhoneNumber {
        return this._state.get('phoneNumber');
    }

    get emailAddress(): ?EmailAddress {
        return this._state.get('emailAddress');
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    get additionalPhoneNumbers(): Immutable.List<PhoneNumber> {
        return this._state.get('additionalPhoneNumbers');
    }

    get additionalEmailAddresses(): Immutable.List<EmailAddress> {
        return this._state.get('additionalEmailAddresses');
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    isHidden(key: string) {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    sanitize(): this {
        return this;
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        const sanitizedContact = super.defaultSanitize();
        if (sanitizedContact) {
            const sanitizedEmailAddress = sanitizedContact.emailAddress?.defaultSanitize();
            const sanitizedLandlineNumber = sanitizedContact.landlineNumber?.defaultSanitize();
            const sanitizedAdditionalPhoneNumbers = sanitizedContact.additionalPhoneNumbers
                .map(phoneNumber => phoneNumber.defaultSanitize())
                .filter(phoneNumber => phoneNumber);
            const sanitizedAdditionalEmailAddresses = sanitizedContact.additionalEmailAddresses
                .map(email => email.defaultSanitize())
                .filter(email => email);
            return sanitizedContact
                .setEmailAddress(sanitizedEmailAddress)
                .setLandlineNumber(sanitizedLandlineNumber)
                .setAdditionalPhoneNumbers(sanitizedAdditionalPhoneNumbers)
                .setAdditionalEmailAddresses(sanitizedAdditionalEmailAddresses);
        }
        return sanitizedContact;
    }

    static defaultInit(): InternalContact {
        const emailAddress = EmailAddress.defaultInit({});
        const phoneNumberInit = PhoneNumber.defaultInit({});
        const additionalEmailAddresses = Immutable.List([emailAddress]);
        const additionalPhoneNumbers = Immutable.List([phoneNumberInit]);
        return InternalContact.fromJS({})
            .setEmailAddress(emailAddress)
            .setLandlineNumber(phoneNumberInit)
            .setAdditionalEmailAddresses(additionalEmailAddresses)
            .setAdditionalPhoneNumbers(additionalPhoneNumbers);
    }

    get landlineNumber(): ?PhoneNumber {
        return this._state.get('landlineNumber');
    }

    get type(): ContactTypeEnum {
        return this._state.get('type');
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }
}

export { InternalContact as Contact };
